import React, { useEffect, useState } from "react";
import { useTranslation } from "@onefront/react-sdk";
import { Field, RadioSub } from "@onefront/react-workspace-components";
import { Box, Skeleton, Stack } from "@vapor/react-material";
import {
    specialVatDeductionRate,
    specialVatRegimeOptions,
    vatExemptionManagementForExporterOptions,
    vatPeriodicityOptions
} from "./constants";
import { BorderedField } from "../../../borderedField";
import { getISODateAndMonth, getISOFromDateAndMonth } from "../../../../utils/functions/date/convert";
import { BusinessFooterForm } from "../footer";
import { LabelWithTooltip } from "@onefront/react-workspace-components/LabelWithTooltip/LabelWithTooltip";
import { ExtendedDatePicker } from "@onefront/workspace-forms";

export const BusinessVatConfigurationForm = ({
    bodyRequest,
    handleChange,
    isEditing,
    onSave,
    onCancel,
    isNewBusiness,
    loading,
    onError,
    displayErrors,
    setDisplayErrors
}) => {
    const { t } = useTranslation();

    const [specialVatRegimeAdditionalFields, setSpecialVatRegimeAdditionalFields] = useState<any>(null);

    useEffect(() => {
        if (bodyRequest?.specialVatRegime) {
            const specialVatRegime = specialVatRegimeOptions(t, isEditing).find(
                option => option.id === bodyRequest?.specialVatRegime
            );
            setSpecialVatRegimeAdditionalFields(specialVatRegime?.subcomponent);
        }
    }, [bodyRequest?.specialVatRegime, t, isEditing]);

    return (
        <Stack gap={isEditing ? 4 : 0} sx={{ width: "740px" }} mb={8}>
            <Box>
                <Field
                    fullWidth
                    component="radio"
                    label={t("business.fields.vatPeriodicity.label")}
                    isEditing={isNewBusiness ? isEditing : false}
                    radioGroupAlign="vertical"
                    onChange={val => {
                        handleChange(val, "vatPeriodicity");
                    }}
                    value={bodyRequest?.vatPeriodicity}
                    options={vatPeriodicityOptions(t)}
                    loading={loading}
                />
            </Box>

            <Box>
                <Field
                    fullWidth
                    component="radio"
                    label={t("business.fields.vatStatementRegistry.label")}
                    isEditing={isEditing}
                    radioGroupAlign="vertical"
                    onChange={val => {
                        handleChange(val, "vatStatementRegistry");
                    }}
                    value={bodyRequest?.vatStatementRegistry}
                    options={[
                        {
                            id: "ON_SALES",
                            value: t("business.fields.vatStatementRegistry.ON_SALES")
                        },
                        {
                            id: "ON_RECEIPT",
                            value: t("business.fields.vatStatementRegistry.ON_RECEIPT")
                        }
                    ]}
                    loading={loading}
                />
            </Box>
            <Box>
                <Stack spacing={2}>
                    <Field
                        fullWidth
                        component="select"
                        label={t("business.fields.specialVatRegime.label")}
                        labelTip={isEditing && t("business.fields.specialVatRegime.description")}
                        isEditing={isEditing}
                        onChange={val => {
                            handleChange(null, "flatTaxAccountingRate");
                            handleChange(false, "cashAccounting");
                            handleChange(val, "specialVatRegime");
                            handleChange(specialVatDeductionRate[val], "vatDeductionRate");
                        }}
                        value={bodyRequest?.specialVatRegime}
                        options={specialVatRegimeOptions(t, isEditing).map(item => {
                            return { id: item.id, value: item.value };
                        })}
                        selectHeight={320}
                        loading={loading}
                    />

                    {bodyRequest?.vatDeductionRate > 0 && (
                        <BorderedField>
                            <Field
                                fullWidth
                                isEditing={false}
                                component="textfield"
                                label={t("business.fields.vatDeductionRate")}
                                value={`${bodyRequest?.vatDeductionRate}%`}
                            />
                        </BorderedField>
                    )}

                    {specialVatRegimeAdditionalFields && (
                        <BorderedField>
                            <Field
                                {...specialVatRegimeAdditionalFields}
                                isEditing={specialVatRegimeAdditionalFields?.isEditing ?? isEditing}
                                value={bodyRequest?.[specialVatRegimeAdditionalFields?.k]}
                                onChange={val => {
                                    const isBool = val === "true" || val === "false";
                                    const finalValue = isBool ? val === "true" : val;
                                    handleChange(finalValue, specialVatRegimeAdditionalFields?.k);
                                    handleChange(null, "cashAccountingExpiry");
                                    setDisplayErrors(prev => ({ ...prev, cashAccountingExpiry: false }));
                                }}
                                sx={{ minWidth: "250px" }}
                                fullWidth
                                additionalProps={{
                                    ...specialVatRegimeAdditionalFields?.additionalProps
                                }}
                            />
                            {bodyRequest?.cashAccounting && (
                                <ExtendedDatePicker
                                    name="cashAccountingExpiry"
                                    label={t("business.fields.cashAccountingExpiry")}
                                    value={getISOFromDateAndMonth?.(bodyRequest?.cashAccountingExpiry)?.toISOString()}
                                    editing={specialVatRegimeAdditionalFields?.isEditing ?? isEditing}
                                    onChange={(key: string, date: string | null) => {
                                        handleChange(getISODateAndMonth(date ? new Date(date) : null), key);
                                        setDisplayErrors(prev => ({ ...prev, cashAccountingExpiry: false }));
                                    }}
                                    onError={error => {
                                        onError("cashAccountingExpiry", error);
                                    }}
                                    displayError={displayErrors?.cashAccountingExpiry}
                                    format="MM/yyyy"
                                    required={true}
                                    views={["month", "year"]}
                                    minDate={new Date().toISOString()}
                                    sx={{ width: "180px" }}
                                />
                            )}
                        </BorderedField>
                    )}
                </Stack>
            </Box>
            <Stack direction="column">
                {!isEditing && loading ? (
                    <Stack spacing={2}>
                        <Skeleton height={24} />
                        <Skeleton height={24} />
                    </Stack>
                ) : (
                    <Box sx={{ my: "16px" }}>
                        <LabelWithTooltip
                            label={t("business.fields.plafondManagement.label")}
                            tooltip={{
                                title: t("business.fields.plafondManagement.tips.title"),
                                message: t("business.fields.plafondManagement.tips.description")
                            }}
                            showTooltip={isEditing}
                        />

                        <RadioSub
                            id="vatExemptionManagementForExporter"
                            isEditing={isEditing}
                            onChange={val => {
                                handleChange(val, "vatExemptionManagementForExporter");
                                handleChange(null, "vatExemptionForExporterStartingDate");
                            }}
                            value={bodyRequest?.vatExemptionManagementForExporter}
                            options={vatExemptionManagementForExporterOptions(t, bodyRequest, handleChange)}
                        />
                    </Box>
                )}
            </Stack>
            {isEditing && (
                <BusinessFooterForm
                    onForward={onSave}
                    onBack={onCancel}
                    loading={loading}
                    disabledForward={loading}
                    isNewBusiness={isNewBusiness}
                />
            )}
        </Stack>
    );
};
